import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import API_URL from '../config/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Check for existing token on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('nightfall_token');
    const storedUser = localStorage.getItem('nightfall_user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      // Verify token is still valid
      verifyToken(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        // Use new token if provided (refresh), otherwise use existing
        if (data.token) {
          setToken(data.token);
          localStorage.setItem('nightfall_token', data.token);
        } else {
          setToken(tokenToVerify);
        }
        // Update user in localStorage
        localStorage.setItem('nightfall_user', JSON.stringify(data.user));
      } else {
        // Only clear on 401 (unauthorized) - not on network errors
        if (response.status === 401) {
          // Token invalid, clear storage
          localStorage.removeItem('nightfall_token');
          localStorage.removeItem('nightfall_user');
          setUser(null);
          setToken(null);
        } else {
          // For other errors (500, etc.), keep the token and user
          // They might be valid, just server issue
          console.warn('Token verification returned non-401 error, keeping token:', response.status);
        }
      }
    } catch (error) {
      // Network errors - don't clear token, might be temporary
      console.warn('Token verification network error (keeping token):', error.message);
      // Keep existing token and user in case it's just a network issue
      // Only clear if we don't have a stored user
      const storedUser = localStorage.getItem('nightfall_user');
      if (!storedUser) {
        localStorage.removeItem('nightfall_token');
        setUser(null);
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok) {
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem('nightfall_token', data.token);
        localStorage.setItem('nightfall_user', JSON.stringify(data.user));
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please check if the server is running.' };
    }
  };

  const register = async (username, email, password) => {
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem('nightfall_token', data.token);
        localStorage.setItem('nightfall_user', JSON.stringify(data.user));
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'Network error. Please check if the server is running.' };
    }
  };

  const loginWithGoogle = async (idToken) => {
    try {
      const response = await fetch(`${API_URL}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken }),
      });

      
      // Check if response is ok before trying to parse JSON
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        return { success: false, error: `Server error: ${response.status} ${response.statusText}` };
      }

      if (response.ok) {
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem('nightfall_token', data.token);
        localStorage.setItem('nightfall_user', JSON.stringify(data.user));
        return { success: true };
      } else {
        console.error('Google auth failed:', data);
        return { success: false, error: data.error || 'Google authentication failed' };
      }
    } catch (error) {
      console.error('Google login network error:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
        API_URL: API_URL
      });
      return { 
        success: false, 
        error: `Network error: ${error.message}. Please check if the server is running on port 5001.` 
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('nightfall_token');
    localStorage.removeItem('nightfall_user');
  };

  const refreshUser = useCallback(async () => {
    const currentToken = token || localStorage.getItem('nightfall_token');
    if (!currentToken) return;

    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${currentToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        localStorage.setItem('nightfall_user', JSON.stringify(data.user));
        // Update token if a new one was provided (refresh)
        if (data.token) {
          setToken(data.token);
          localStorage.setItem('nightfall_token', data.token);
        }
      } else if (response.status === 401) {
        // Only clear on actual auth failure
        localStorage.removeItem('nightfall_token');
        localStorage.removeItem('nightfall_user');
        setUser(null);
        setToken(null);
      }
      // For other errors, silently fail - don't clear token
    } catch (error) {
      // Network errors - don't clear token, might be temporary
      console.warn('Failed to refresh user (network error, keeping token):', error.message);
    }
  }, [token]);

  const updateToken = (newToken) => {
    setToken(newToken);
    localStorage.setItem('nightfall_token', newToken);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    loginWithGoogle,
    logout,
    refreshUser,
    updateToken,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

